// src/Dashboard/pages/teacher/SuiviCompetitions/components/CompetitionUnregisterDrawer.jsx

import React, { useEffect, useMemo, useState } from "react";
import {
  FaCheck,
  FaFilter,
  FaMinus,
  FaSearch,
  FaTimes,
  FaUserTimes,
} from "react-icons/fa";
import { toast } from "react-toastify";

import { unregisterAdherent } from "../../../../../services/eventServices";

import {
  CATE_EVENTS,
  formatWeight,
  getAdherentCompetitionCategories,
  getAdherentName,
  getAdherentPhoto,
  getBirthYear,
  uniqSorted,
} from "../utils/suiviCompetitionsUtils";

const getGroupsLabel = (adherent) => {
  const groups = Array.isArray(adherent?.Groups) ? adherent.Groups : [];

  return groups
    .map((group) => group?.nom)
    .filter(Boolean)
    .join(", ");
};

function RegisteredAdherentUnregisterCard({
  adherent,
  selected,
  onToggle,
}) {
  const photo = getAdherentPhoto(adherent);
  const name = getAdherentName(adherent);
  const groupsLabel = getGroupsLabel(adherent);
  const birthYear = getBirthYear(adherent?.date_naissance);
  const { ageCategory, weightCategory } =
    getAdherentCompetitionCategories(adherent);

  return (
    <button
      type="button"
      onClick={() => onToggle(adherent.id)}
      className={`relative flex min-h-[108px] items-start gap-3 rounded-[24px] border p-3 text-left transition ${
        selected
          ? "border-red-300 bg-red-50 ring-2 ring-red-300/50 dark:border-red-500/40 dark:bg-red-500/10 dark:ring-red-500/20"
          : "border-slate-200 bg-white hover:-translate-y-0.5 hover:bg-slate-50 hover:shadow-md dark:border-slate-700 dark:bg-slate-950 dark:hover:bg-slate-900"
      }`}
    >
      <img
        src={photo}
        alt={name}
        className="h-16 w-16 shrink-0 rounded-2xl object-cover ring-1 ring-slate-200 dark:ring-slate-700"
        onError={(event) => {
          event.currentTarget.src = "/img/user_avatar.jpg";
        }}
      />

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-bold text-slate-900 dark:text-white">
          {name}
        </p>

        <div className="mt-1 flex flex-wrap gap-1.5">
          <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-bold text-blue-700 ring-1 ring-blue-200 dark:bg-blue-900/20 dark:text-blue-200 dark:ring-blue-800/50">
            {ageCategory || "Inconnu"}
          </span>

          <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-200 dark:ring-emerald-800/50">
            {birthYear || "Année ?"}
          </span>

          <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-bold text-amber-700 ring-1 ring-amber-200 dark:bg-amber-900/20 dark:text-amber-200 dark:ring-amber-800/50">
            {weightCategory || "Poids ?"}
          </span>
        </div>

        <p className="mt-2 line-clamp-1 text-[11px] text-slate-500 dark:text-slate-400">
          {groupsLabel || "Sans groupe"}
        </p>

        <p className="mt-1 text-[11px] text-slate-400">
          Poids enregistré : {formatWeight(adherent?.poids)}
        </p>
      </div>

      {selected && (
        <span className="absolute right-3 top-3 grid h-8 w-8 place-items-center rounded-xl bg-red-600 text-white shadow">
          <FaUserTimes />
        </span>
      )}
    </button>
  );
}

export default function CompetitionUnregisterDrawer({
  isOpen,
  event,
  adherents = [],
  onClose,
  onUnregistered,
}) {
  const [search, setSearch] = useState("");
  const [ageCategoryFilter, setAgeCategoryFilter] = useState("");
  const [birthYearFilter, setBirthYearFilter] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    setSearch("");
    setAgeCategoryFilter("");
    setBirthYearFilter("");
    setSelectedIds(new Set());

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKey = (keyboardEvent) => {
      if (keyboardEvent.key === "Escape") onClose?.();
    };

    window.addEventListener("keydown", onKey);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [isOpen, onClose]);

  const enrichedAdherents = useMemo(() => {
    return (adherents || []).map((adherent) => {
      const categories = getAdherentCompetitionCategories(adherent);

      return {
        ...adherent,
        _name: getAdherentName(adherent),
        _groupsLabel: getGroupsLabel(adherent),
        _birthYear: categories.birthYear,
        _ageCategory: categories.ageCategory,
        _weightCategory: categories.weightCategory,
      };
    });
  }, [adherents]);

  const ageCategoryOptions = useMemo(() => {
    const found = uniqSorted(
      enrichedAdherents.map((adherent) => adherent._ageCategory)
    );

    const ordered = CATE_EVENTS.filter((category) => found.includes(category));
    const others = found.filter((category) => !ordered.includes(category));

    return [...ordered, ...others];
  }, [enrichedAdherents]);

  const birthYearOptions = useMemo(() => {
    return uniqSorted(
      enrichedAdherents
        .map((adherent) => adherent._birthYear)
        .filter(Boolean)
    );
  }, [enrichedAdherents]);

  const filteredAdherents = useMemo(() => {
    const query = search.trim().toLowerCase();

    return enrichedAdherents
      .filter((adherent) => {
        const matchesSearch =
          !query ||
          [
            adherent._name,
            adherent.licence_number,
            adherent._groupsLabel,
            adherent._ageCategory,
            adherent._birthYear,
            adherent._weightCategory,
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase()
            .includes(query);

        const matchesAgeCategory =
          !ageCategoryFilter || adherent._ageCategory === ageCategoryFilter;

        const matchesBirthYear =
          !birthYearFilter ||
          String(adherent._birthYear) === String(birthYearFilter);

        return matchesSearch && matchesAgeCategory && matchesBirthYear;
      })
      .sort((a, b) => a._name.localeCompare(b._name, "fr"));
  }, [enrichedAdherents, search, ageCategoryFilter, birthYearFilter]);

  const selectedAdherents = useMemo(() => {
    return enrichedAdherents.filter((adherent) => selectedIds.has(adherent.id));
  }, [enrichedAdherents, selectedIds]);

  const toggleOne = (id) => {
    setSelectedIds((previous) => {
      const next = new Set(previous);

      if (next.has(id)) next.delete(id);
      else next.add(id);

      return next;
    });
  };

  const selectVisible = () => {
    setSelectedIds((previous) => {
      const next = new Set(previous);

      filteredAdherents.forEach((adherent) => {
        next.add(adherent.id);
      });

      return next;
    });
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
  };

  const resetFilters = () => {
    setSearch("");
    setAgeCategoryFilter("");
    setBirthYearFilter("");
  };

  const handleSubmit = async () => {
    if (!event?.id) {
      toast.error("Événement manquant.");
      return;
    }

    const ids = Array.from(selectedIds).map(Number);

    if (ids.length === 0) {
      toast.error("Veuillez sélectionner au moins un adhérent.");
      return;
    }

    if (
      !window.confirm(
        `Désinscrire ${ids.length} adhérent${ids.length > 1 ? "s" : ""} de cette compétition ?`
      )
    ) {
      return;
    }

    try {
      setSaving(true);

      const results = await Promise.allSettled(
        ids.map((adherentId) => unregisterAdherent(adherentId, event.id))
      );

      const success = results.filter((result) => result.status === "fulfilled")
        .length;
      const failed = results.length - success;

      if (success && !failed) {
        toast.success(
          `${success} adhérent${success > 1 ? "s" : ""} désinscrit${
            success > 1 ? "s" : ""
          }.`
        );
      } else if (success && failed) {
        toast.warn(`${success} désinscription(s), ${failed} erreur(s).`);
      } else {
        toast.error("Aucune désinscription n’a pu être enregistrée.");
      }

      setSelectedIds(new Set());
      await onUnregistered?.();

      if (success > 0 && failed === 0) {
        onClose?.();
      }
    } catch (error) {
      console.error("Erreur désinscription :", error);
      toast.error("Erreur lors de la désinscription.");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen || !event) return null;

  return (
    <div className="fixed inset-0 z-[100]">
      <div
        className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      <aside className="absolute inset-y-0 right-0 flex w-full max-w-6xl flex-col overflow-hidden bg-white shadow-2xl dark:bg-slate-900">
        <div className="h-1.5 bg-gradient-to-r from-red-500 via-rose-500 to-orange-500" />

        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3 dark:border-slate-800">
          <div className="min-w-0">
            <p className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Désinscription compétition
            </p>

            <h2 className="truncate text-lg font-bold text-slate-900 dark:text-white">
              {event?.titre || "Événement"}
            </h2>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-700 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
            aria-label="Fermer"
          >
            <FaTimes />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <div className="rounded-[24px] border border-red-200 bg-red-50 p-4 text-sm text-red-800 dark:border-red-800/50 dark:bg-red-900/10 dark:text-red-100">
            Pensez à enregistrer les résultats et le palmarès avant de
            désinscrire un adhérent.
          </div>

          <div className="mt-4 rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900/60">
            <div className="mb-3 flex items-center gap-2 text-sm font-bold text-slate-900 dark:text-white">
              <FaFilter className="text-red-500" />
              Filtres des inscrits
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
              <div className="xl:col-span-2">
                <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Recherche
                </label>

                <div className="relative">
                  <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

                  <input
                    type="text"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Nom, prénom, licence, groupe..."
                    className="w-full rounded-2xl border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-red-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                  />
                </div>
              </div>

              <SelectField
                label="Catégorie"
                value={ageCategoryFilter}
                onChange={setAgeCategoryFilter}
                options={ageCategoryOptions}
                emptyLabel="Toutes"
              />

              <SelectField
                label="Année"
                value={birthYearFilter}
                onChange={setBirthYearFilter}
                options={birthYearOptions}
                emptyLabel="Toutes"
              />
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={selectVisible}
                className="inline-flex items-center gap-2 rounded-2xl bg-red-600 px-4 py-2 text-xs font-bold text-white transition hover:bg-red-700"
              >
                <FaCheck />
                Sélectionner visibles
              </button>

              <button
                type="button"
                onClick={clearSelection}
                className="inline-flex items-center gap-2 rounded-2xl bg-white px-4 py-2 text-xs font-bold text-slate-700 ring-1 ring-slate-200 transition hover:bg-slate-50 dark:bg-slate-950 dark:text-slate-200 dark:ring-slate-700 dark:hover:bg-slate-800"
              >
                <FaTimes />
                Vider
              </button>

              <button
                type="button"
                onClick={resetFilters}
                className="inline-flex items-center gap-2 rounded-2xl bg-white px-4 py-2 text-xs font-bold text-slate-700 ring-1 ring-slate-200 transition hover:bg-slate-50 dark:bg-slate-950 dark:text-slate-200 dark:ring-slate-700 dark:hover:bg-slate-800"
              >
                Réinitialiser filtres
              </button>
            </div>
          </div>

          <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Inscrits
              </p>

              <h3 className="mt-1 text-lg font-bold text-slate-900 dark:text-white">
                {filteredAdherents.length} résultat
                {filteredAdherents.length > 1 ? "s" : ""}
              </h3>
            </div>

            {selectedAdherents.length > 0 && (
              <div className="rounded-2xl bg-red-50 px-4 py-2 text-sm font-bold text-red-700 ring-1 ring-red-200 dark:bg-red-900/20 dark:text-red-200 dark:ring-red-800/60">
                {selectedAdherents.length} à désinscrire
              </div>
            )}
          </div>

          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {filteredAdherents.length === 0 ? (
              <div className="col-span-full rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-800/40 dark:text-slate-400">
                Aucun inscrit ne correspond aux filtres.
              </div>
            ) : (
              filteredAdherents.map((adherent) => (
                <RegisteredAdherentUnregisterCard
                  key={adherent.id}
                  adherent={adherent}
                  selected={selectedIds.has(adherent.id)}
                  onToggle={toggleOne}
                />
              ))
            )}
          </div>
        </div>

        <div className="border-t border-slate-200 bg-white/95 p-3 dark:border-slate-800 dark:bg-slate-900/95">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-sm text-slate-500 dark:text-slate-400">
              Sélection :{" "}
              <span className="font-bold text-slate-900 dark:text-white">
                {selectedIds.size}
              </span>{" "}
              adhérent{selectedIds.size > 1 ? "s" : ""}
            </div>

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                <FaTimes />
                Annuler
              </button>

              <button
                type="button"
                onClick={handleSubmit}
                disabled={saving || selectedIds.size === 0}
                className="inline-flex items-center gap-2 rounded-2xl bg-red-600 px-4 py-3 text-sm font-bold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <FaMinus />
                {saving
                  ? "Désinscription..."
                  : `Désinscrire ${selectedIds.size || ""}`}
              </button>
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
}

function SelectField({ label, value, onChange, options = [], emptyLabel }) {
  return (
    <div>
      <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
      </label>

      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-red-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
      >
        <option value="">{emptyLabel}</option>

        {(options || [])
          .filter((item) => item !== null && item !== undefined)
          .map((item) => (
            <option key={String(item || "empty")} value={item}>
              {item || emptyLabel}
            </option>
          ))}
      </select>
    </div>
  );
}